import React from 'react';

// Card Component
export const Card = ({ children, className = '' }) => {
  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {children}
    </div>
  );
};

// Button Component
export const Button = ({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  type = 'button',
  className = '',
  title
}) => {
  const baseStyles = 'rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300',
    secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500 disabled:bg-gray-100',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    small: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    medium: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    large: 'px-6 py-3 text-lg'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
};

// Alert Component
export const Alert = ({ type = 'info', message, onClose }) => {
  const typeStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800'
  };

  const iconMap = {
    info: 'fa-info-circle',
    success: 'fa-check-circle',
    warning: 'fa-exclamation-triangle',
    error: 'fa-exclamation-circle'
  };

  return (
    <div className={`border rounded-lg p-4 ${typeStyles[type]} flex items-start justify-between`}>
      <div className="flex items-start">
        <i className={`fas ${iconMap[type]} mt-0.5 mr-3`}></i>
        <span className="text-sm">{message}</span>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 ml-4"
        >
          <i className="fas fa-times"></i>
        </button>
      )}
    </div>
  );
};

// Tabs Component
export const Tabs = ({ tabs, activeTab, onChange }) => {
  return (
    <div className="border-b border-gray-200">
      <nav className="flex space-x-8" aria-label="Tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
              ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {tab.icon && <i className={`fas fa-${tab.icon} mr-2`}></i>}
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

// Input Component
export const Input = ({
  label,
  value,
  onChange,
  onKeyPress,
  placeholder,
  type = 'text',
  required = false,
  multiline = false,
  rows = 3,
  className = '',
  fullWidth = false
}) => {
  const inputStyles = `${fullWidth ? 'w-full' : ''} px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
  const isTextarea = multiline || type === 'textarea';

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {isTextarea ? (
        <textarea
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          required={required}
          rows={rows}
          className={inputStyles}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          required={required}
          className={inputStyles}
        />
      )}
    </div>
  );
};

// Select Component
export const Select = ({
  label,
  value,
  onChange,
  options,
  required = false,
  className = '',
  fullWidth = false,
  disabled = false,
  children,
  ...props
}) => {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={`${fullWidth ? 'w-full' : ''} px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed`}
        {...props}
      >
        {children ? children : options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Modal Component
export const Modal = ({ isOpen = true, title, children, onClose, onSubmit, size = 'md', footer }) => {
  // Don't render if not open
  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit();
    }
  };

  const sizeClasses = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl'
  };

  const content = (
    <>
      {/* Header */}
      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            {typeof title === 'string' ? (
              <h3 className="text-lg font-medium text-gray-900">
                {title}
              </h3>
            ) : (
              title
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 ml-4"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Content */}
        <div>{children}</div>
      </div>

      {/* Footer */}
      {footer ? (
        <div className="bg-gray-50 px-4 py-3 sm:px-6">
          {footer}
        </div>
      ) : onSubmit ? (
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <Button
            type="submit"
            variant="primary"
            className="w-full sm:w-auto sm:ml-3"
          >
            Create
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="w-full sm:w-auto mt-3 sm:mt-0"
          >
            Cancel
          </Button>
        </div>
      ) : null}
    </>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal Panel */}
        <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${sizeClasses[size]} sm:w-full z-50 relative`}>
          {onSubmit ? (
            <form onSubmit={handleSubmit}>
              {content}
            </form>
          ) : (
            content
          )}
        </div>
      </div>
    </div>
  );
};

// Loader Component
export const Loader = ({ message = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-3">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600">{message}</p>
    </div>
  );
};

// Badge Component
export const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    primary: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};
